import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@ect/core";
import { useSession } from "./session";

export type ToastMsg = { kind: "ok" | "error"; text: string } | null;
/** One toast channel for the whole shell: any useApi() instance flashes, the shell's <Toast/> shows it. */
const listeners = new Set<(t: ToastMsg) => void>();
let timer: ReturnType<typeof setTimeout> | null = null;
export function flashToast(kind: "ok" | "error", text: string) {
  listeners.forEach((l) => l({ kind, text }));
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => listeners.forEach((l) => l(null)), kind === "ok" ? 2200 : 6000);
}
export function useToast(): ToastMsg {
  const [t, setT] = useState<ToastMsg>(null);
  useEffect(() => { listeners.add(setT); return () => void listeners.delete(setT); }, []);
  return t;
}

/** Admin writes go through the REST API (the replica catches up within a second); reads stay local. */
export function useApi() {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const flash = useCallback((kind: "ok" | "error", text: string) => flashToast(kind, text), []);
  const call = useCallback(async <T,>(path: string, init?: RequestInit, okMessage = "Enregistré"): Promise<T | undefined> => {
    setBusy(true);
    try {
      const r = await api.json<T>(path, init);
      flash("ok", okMessage);
      return r;
    } catch (e) {
      const body = (e as ApiError).body as { message?: string | { message?: string }; issues?: { path: string; message: string }[] } | null;
      const msg = e instanceof ApiError ? (typeof body?.message === "string" ? body.message : body?.message?.message) ?? (body?.issues?.map((i) => `${i.path}: ${i.message}`).join(", ") || `Erreur ${e.status}`) : "Hors ligne : l'action nécessite une connexion.";
      flash("error", msg);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, [api, flash]);
  const post = useCallback(<T,>(path: string, body: unknown, ok?: string) => call<T>(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }, ok), [call]);
  const patch = useCallback(<T,>(path: string, body: unknown, ok?: string) => call<T>(path, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }, ok), [call]);
  const put = useCallback(<T,>(path: string, body: unknown, ok?: string) => call<T>(path, { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }, ok), [call]);
  return { api, busy, toast, call, post, patch, put };
}

export function Toast({ toast }: { toast: { kind: "ok" | "error"; text: string } | null }) {
  if (!toast) return null;
  return <div role="status" className={`fixed bottom-4 start-1/2 z-50 -translate-x-1/2 rounded-md px-4 py-2 text-sm font-medium shadow-soft rtl:translate-x-1/2 ${toast.kind === "ok" ? "bg-green-soft text-green" : "bg-[#fbe3e3] text-danger"}`}>{toast.text}</div>;
}
